<template>
  <Loader v-if="isLoading" />
  <Page v-else>
    <Breadcrumbs root-path="/certifications" :showJumpTo="true">
      <template #loanservice-banner v-if="movedToLoanService">
        <div class="icon-circle-check loanservice-banner">
          This application has been completed and moved to Loan Service.
        </div>
      </template>
      <template #jumpto>
        <div class="jumpto-container">
          <flyout-menu title="Jump To" :items="menuLinks"></flyout-menu>
        </div>
      </template>
    </Breadcrumbs>
    <template v-if="certification">
      <div class="container" v-if="certification">
        <Header :certification="certification"></Header>
      </div>
      <CertificationsAndDisbursements
        :certification="certification"
        @commentAdded="handleCommentAdded"
        @certificationUpdated="handleCertificationUpdated($event)"
        @disbursementAdded="handleDisbursementAdded($event)"
        @disbursementUpdated="handleDisbursementUpdated($event)"
      />
    </template>
  </Page>
</template>

<script setup lang="ts">
import { onMounted, ref, computed, watch } from "vue";
import { useRoute } from "vue-router";
import Loader from "@/components/Loader.vue";
import Page from "@/components/v2/layout/Page.vue";
import Breadcrumbs from "@/components/shared/Breadcrumbs.vue";
import Header from "@/components/v2/certifications/Header.vue";
import FlyoutMenu from "@/components/v2/shared/FlyoutMenu.vue";
import CertificationsAndDisbursements from "@/components/v2/certifications/CertificationsAndDisbursements.vue";
import { CertificationVM } from "@/types/certifications";
import { JumpToMenuLink } from "@/types/jumpto-menu";
import { useCertification } from "@/composables/certification/useCertification";
import { DisbursementVM, DisbursementAddedEvent } from "@/types/disbursements";
import { $api } from "@/services/api1";
import functions from "@/use/functions.js";

const route = useRoute();

const certificationId = ref<number>();

const isLoading = ref<boolean>(true);

const movedToLoanService = computed<boolean>(
  () => certification.value?.appMovedToLoanSvc || false
);

const certification = ref<CertificationVM>();

const links = computed<Array<JumpToMenuLink>>(
  () => certification.value?.jumpToMenuLinks || []
);

const menuLinks = computed(() => links.value.filter((l: any) => l.visible));

const { getCertification } = useCertification();

function setCertificationId() {
  const id = route.params.id;
  certificationId.value = Number(id);
}

function setCertification(certificationVM: CertificationVM) {
  certification.value = certificationVM;
}

async function handleCommentAdded() {
  if (certification.value) {
    const commentData = await $api.certifications.getComments({
      certificationId: certification.value.form.id,
      appId: certification.value.appId,
      loanId: certification.value.loanId,
    });

    certification.value.comments = commentData;
  }
}

async function handleCertificationUpdated(
  updatedCertification: CertificationVM
) {
  await initialize("certificationUpdated");
  functions.closeModal();
}

async function handleDisbursementUpdated(event: {
  disbursement: DisbursementVM;
  disbursements: DisbursementVM[];
}) {
  if (certification.value) {
    const response = await getCertification(certificationId.value);

    if (response?.success) {
      // if any other disb rows are being edited - leave them in edit mode - do not reset them
      const index = certification.value?.disbursements.findIndex(
        (d: DisbursementVM) => d.details.id === event.disbursement.details.id
      );

      if (index !== -1) {
        certification.value.disbursements[index] = event.disbursement;
      }

      response.results.disbursements = certification.value?.disbursements;

      setCertification(response.results);
    }
  }

  functions.closeModal();
}

async function handleDisbursementAdded(data: DisbursementAddedEvent) {
  if (certification.value) {
    if (data.disbursementIndex !== -1) {
      certification.value.disbursements[data.disbursementIndex] =
        data.disbursement;
    }
  }

  await initialize("disbursementAdded");

  functions.closeModal();
}

async function initialize(listener?: string) {
  setCertificationId();
  const response = await getCertification(certificationId.value);
  if (response?.success) {
    if (listener !== undefined && listener === "disbursementUpdated") {
      response.results.disbursements = certification.value?.disbursements;
    }

    if (listener !== undefined && listener === "disbursementAdded") {
      response.results.disbursements = certification.value?.disbursements;
    }

    if (listener !== undefined && listener === "commentAdded") {
      response.results.disbursements = certification.value?.disbursements;
      response.results.form = certification.value?.form;
      response.results.initialFormValues =
        certification.value?.initialFormValues;
    }

    setCertification(response.results);
  } else {
    functions.openModal({
      title: "Oops!",
      description:
        "There was an issue getting the Certification & Disbursements. Please try again later.",
    });
  }

  isLoading.value = false;
}
onMounted(async () => {
  await initialize();
});

watch(
  () => route.params.id,
  async (newId, oldId) => {
    console.log("certification id", newId);
    certificationId.value = Number(newId);
    if (newId !== oldId) {
      isLoading.value = true;
      await initialize();
    }
  }
);
</script>

<style scoped>
* {
  font-family: var(--sc-montserrat-font-family);
  font-weight: 700;
}

.bg-white {
  background-color: #ffffff;
}

div.xpage-inner {
  padding: 0;
  background-color: #ecedef;
}

div.sc-page-inner {
  padding-left: 15px;
}

div.xpage-breadcrumbs {
  height: 46px;
  line-height: 46px;
  background: hsl(227deg 12% 85%);
}

.jumpto-container {
  float: right;
}

.icon-circle-check:before {
  color: #fff;
}

.loanservice-banner {
  display: block;
  font-weight: 600;
  font-size: 12px;
  border-radius: 20px;
  background: #48a842;
  color: #fff;
  padding: 5px 9px;
}

@media screen and (max-width: 992px) {
  div.xpage-inner {
    padding: 15px;
  }
}
</style>
